import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { companyAPI, settingsAPI, leaveAPI, attendanceAPI, kioskAPI, ptAPI } from '../../lib/api';
import { Checkbox } from '../../components/ui/checkbox';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '../../components/ui/dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ConfirmationDialog, useConfirmation } from '../../components/ui/confirmation-dialog';
import { Loader2, Save, Building2, Plus, Trash2, Edit, Eye, Monitor, Keyboard, Package, Smartphone, Wifi, HardDrive, MapPin, Gift, CalendarX, Mail, Bell, Clock, DollarSign, Users, Calendar, ShieldCheck, Briefcase, Calculator } from 'lucide-react';
import ModuleDisabled from '../../components/shared/ModuleDisabled';
import { calculatePFEstimate } from '../../lib/payrollUtils';

// IST = UTC + 5:30 (330 minutes)
const utcToIst = (utcHHMM) => {
    const [h, m] = (utcHHMM || '00:00').split(':').map(Number);
    const total = ((h * 60 + m + 330) + 1440) % 1440;
    return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
};
const istToUtc = (istHHMM) => {
    const [h, m] = (istHHMM || '00:00').split(':').map(Number);
    const total = ((h * 60 + m - 330) + 1440) % 1440;
    return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
};

const Settings = () => {
    const { user } = useAuth();
    const { dialogProps, confirm } = useConfirmation();
    const [activeTab, setActiveTab] = useState('general');
    const [activeSection, setActiveSection] = useState('company');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // General Tab States
    const [company, setCompany] = useState(null);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState({});
    const [pfTestBasic, setPfTestBasic] = useState(15000);
    const [pfTestDa, setPfTestDa] = useState(0);
    const [pfTestLop, setPfTestLop] = useState(0);

    // Departments & Designations States
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);
    const [isDeptOpen, setIsDeptOpen] = useState(false);
    const [isDesgOpen, setIsDesgOpen] = useState(false);
    const [createLoading, setCreateLoading] = useState(false);
    const [editingDept, setEditingDept] = useState(null);
    const [editingDesg, setEditingDesg] = useState(null);
    const [deptForm, setDeptForm] = useState({ name: '', code: '' });
    const [desgForm, setDesgForm] = useState({ name: '', department_id: '' });

    // Leave Types States
    const [leaveTypes, setLeaveTypes] = useState([]);
    const [isLeaveTypeOpen, setIsLeaveTypeOpen] = useState(false);
    const [editingLeaveType, setEditingLeaveType] = useState(null);
    const [leaveTypeForm, setLeaveTypeForm] = useState({
        name: '', code: '', description: '', days_allowed: 0,
        carry_forward: false, max_carry_forward_days: 0, is_paid: true, is_active: true, pro_rata: false, min_tenure_months: 0
    });
    const [leaveRefreshCycle, setLeaveRefreshCycle] = useState('calendar_year');
    const [allowedOptionalHolidays, setAllowedOptionalHolidays] = useState(0);
    const [savingLeaveSettings, setSavingLeaveSettings] = useState(false);

    // Asset Categories States
    const [assetCategories, setAssetCategories] = useState([]);
    const [isAssetCatOpen, setIsAssetCatOpen] = useState(false);
    const [editingAssetCat, setEditingAssetCat] = useState(null);
    const [assetCatForm, setAssetCatForm] = useState({ name: '', code: '', description: '', icon: '' });

    // Locations States
    const [locations, setLocations] = useState([]);
    const [isLocOpen, setIsLocOpen] = useState(false);
    const [editingLoc, setEditingLoc] = useState(null);
    const [locForm, setLocForm] = useState({ name: '', description: '', latitude: '', longitude: '', radius: 100 });
    const [mapsUrl, setMapsUrl] = useState('');
    const [resolvingCoords, setResolvingCoords] = useState(false);

    // Attendance Config States
    const [attendanceConfig, setAttendanceConfig] = useState(null);
    const [savingAttendance, setSavingAttendance] = useState(false);

    // Notification Preferences States
    const [preferences, setPreferences] = useState({ celebration_email_hour: 0 });
    const [celebrationTime, setCelebrationTime] = useState('00:00');
    const [savingPreferences, setSavingPreferences] = useState(false);

    // Kiosk / FRA States
    const [ptStates, setPtStates] = useState([]);
    const [enrolling, setEnrolling] = useState(false);
    const [enrollResult, setEnrollResult] = useState(null);
    const [resetting, setResetting] = useState(false);

    const ICON_OPTIONS = [
        { value: 'Monitor', label: 'Monitor', icon: Monitor },
        { value: 'Keyboard', label: 'Keyboard', icon: Keyboard },
        { value: 'Smartphone', label: 'Smartphone', icon: Smartphone },
        { value: 'Wifi', label: 'Network', icon: Wifi },
        { value: 'HardDrive', label: 'Storage', icon: HardDrive },
        { value: 'Package', label: 'Package', icon: Package },
    ];

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            // Fetch all needed data
            const [companyRes, deptRes, desgRes, leaveRes] = await Promise.all([
                companyAPI.getCurrent(),
                settingsAPI.getDepartments(),
                settingsAPI.getDesignations(),
                leaveAPI.getTypes({ is_active: null }) // Fetch all
            ]);

            setCompany(companyRes.data);
            setFormData(companyRes.data);
            setLeaveRefreshCycle(companyRes.data.leave_refresh_cycle || 'calendar_year');
            setAllowedOptionalHolidays(companyRes.data.allowed_optional_holidays || 0);
            setDepartments(deptRes.data);
            setDesignations(desgRes.data);
            setLeaveTypes(leaveRes.data);
            
            // Fetch locations
            try {
                const locRes = await settingsAPI.getLocations();
                setLocations(locRes.data);
            } catch (e) {
                setLocations([]);
            }

            // Fetch asset categories
            try {
                const assetCatRes = await settingsAPI.getAssetCategories();
                // Ensure we have valid array with proper objects
                if (Array.isArray(assetCatRes.data) && assetCatRes.data.every(cat => typeof cat.name === 'string')) {
                    setAssetCategories(assetCatRes.data);
                } else {
                    setAssetCategories([]);
                }
            } catch (e) {
                // If module not enabled or error, set empty
                setAssetCategories([]);
            }

            // Fetch attendance config
            try {
                const attRes = await attendanceAPI.getConfig();
                setAttendanceConfig(attRes.data);
            } catch (e) {
                setAttendanceConfig({ company_id: companyRes.data.id });
            }

            // Fetch notification preferences
            try {
                const prefsRes = await settingsAPI.getPreferences();
                setPreferences(prefsRes.data);
                const utcTime = prefsRes.data?.celebration_email_time || '00:00';
                setCelebrationTime(utcToIst(utcTime));
            } catch (e) {
                setPreferences({ celebration_email_time: '00:00' });
                setCelebrationTime('05:30'); // 00:00 UTC = 05:30 IST
            }

            // Fetch Professional Tax enabled states
            try {
                const ptRes = await ptAPI.getConfigs({ enabled_only: true });
                setPtStates(ptRes.data || []);
            } catch (e) {
                setPtStates([]);
            }
        } catch (error) {
            if (error.response?.status === 403) {
                setError(error.response.data.detail);
            } else {
                toast.error('Failed to fetch settings');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleLogoUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);

        setSaving(true);
        try {
            const response = await companyAPI.uploadLogo(formData);
            setFormData({ ...formData, logo: response.data.logo_url });
            setCompany({ ...company, logo: response.data.logo_url });
            toast.success('Logo uploaded successfully');
        } catch (error) {
            toast.error('Failed to upload logo');
        } finally {
            setSaving(false);
        }
    };

    const handleSaveGeneral = async () => {
        setSaving(true);
        try {
            await companyAPI.updateCurrent({
                name: formData.name,
                email: formData.email,
                phone: formData.phone,
                address: formData.address,
                city: formData.city,
                state: formData.state,
                pincode: formData.pincode,
                pf_number: formData.pf_number,
                esi_number: formData.esi_number,
                tan: formData.tan,
                pan: formData.pan,
                pt_state: formData.pt_state,
                bank_name: formData.bank_name,
                bank_account_number: formData.bank_account_number,
                bank_ifsc: formData.bank_ifsc,
                bank_branch: formData.bank_branch,
                pf_contribution: formData.pf_contribution,
                fnf_leave_encashment_enabled: formData.fnf_leave_encashment_enabled ?? true,
                attendance_payroll_config: formData.attendance_payroll_config,
            });
            toast.success('Settings saved successfully');

            const res = await companyAPI.getCurrent();
            setCompany(res.data);
            setFormData(res.data);
        } catch (error) {
            toast.error('Failed to save settings');
        } finally {
            setSaving(false);
        }
    };

    const handleSavePreferences = async () => {
        const utcTime = istToUtc(celebrationTime);
        setSavingPreferences(true);
        try {
            const res = await settingsAPI.updatePreferences({
                celebration_email_time: utcTime,
                celebration_email_enabled: preferences?.celebration_email_enabled !== false
            });
            setPreferences(res.data);
            setCelebrationTime(utcToIst(res.data?.celebration_email_time || utcTime));

            if (attendanceConfig) {
                const payload = {
                    ...attendanceConfig,
                    company_id: company.id
                };
                const attRes = await attendanceAPI.updateConfig(payload);
                setAttendanceConfig(attRes.data);
            }

            toast.success('Notification preferences saved');
        } catch (error) {
            toast.error('Failed to save preferences');
        } finally {
            setSavingPreferences(false);
        }
    };

    const handleCreateDept = async (e) => {
        e.preventDefault();
        setCreateLoading(true);
        try {
            if (editingDept) {
                await settingsAPI.updateDepartment(editingDept.id, deptForm);
                toast.success('Department updated');
            } else {
                await settingsAPI.createDepartment(deptForm);
                toast.success('Department created');
            }
            setIsDeptOpen(false);
            setEditingDept(null);
            setDeptForm({ name: '', code: '' });
            // Refresh list
            const res = await settingsAPI.getDepartments();
            setDepartments(res.data);
        } catch (error) {
            const detail = error.response?.data?.detail;
            const msg = typeof detail === 'string' ? detail :
                Array.isArray(detail) ? detail.map(e => e.msg).join(', ') : 'Failed to save department';
            toast.error(msg);
        } finally {
            setCreateLoading(false);
        }
    };

    const handleEditDept = (dept) => {
        setEditingDept(dept);
        setDeptForm({ name: dept.name, code: dept.code || '' });
        setIsDeptOpen(true);
    };

    const handleDeleteDept = async (id) => {
        const confirmed = await confirm({
            title: 'Delete Department',
            description: 'Are you sure? This might affect employees assigned to this department.',
            confirmText: 'Delete',
            variant: 'destructive',
        });
        if (!confirmed) return;
        try {
            await settingsAPI.deleteDepartment(id);
            toast.success('Department deleted');
            setDepartments(departments.filter(d => d.id !== id));
        } catch (error) {
            toast.error('Failed to delete department');
        }
    };

    const handleCreateDesg = async (e) => {
        e.preventDefault();
        setCreateLoading(true);
        try {
            // Fix for "None" selection
            const payload = { ...desgForm };
            if (payload.department_id === "none") payload.department_id = null;

            if (editingDesg) {
                await settingsAPI.updateDesignation(editingDesg.id, payload);
                toast.success('Designation updated');
            } else {
                await settingsAPI.createDesignation(payload);
                toast.success('Designation created');
            }
            setIsDesgOpen(false);
            setEditingDesg(null);
            setDesgForm({ name: '', department_id: '' });
            // Refresh list
            const res = await settingsAPI.getDesignations();
            setDesignations(res.data);
        } catch (error) {
            const detail = error.response?.data?.detail;
            const msg = typeof detail === 'string' ? detail :
                Array.isArray(detail) ? detail.map(e => e.msg).join(', ') : 'Failed to save designation';
            toast.error(msg);
        } finally {
            setCreateLoading(false);
        }
    };

    const handleEditDesg = (desg) => {
        setEditingDesg(desg);
        setDesgForm({ name: desg.name, department_id: desg.department_id || 'none' });
        setIsDesgOpen(true);
    };

    const handleDeleteDesg = async (id) => {
        const confirmed = await confirm({
            title: 'Delete Designation',
            description: 'Are you sure you want to delete this designation?',
            confirmText: 'Delete',
            variant: 'destructive',
        });
        if (!confirmed) return;
        try {
            await settingsAPI.deleteDesignation(id);
            toast.success('Designation deleted');
            setDesignations(designations.filter(d => d.id !== id));
        } catch (error) {
            toast.error('Failed to delete designation');
        }
    };

    const handleEditLeaveType = (lt) => {
        setEditingLeaveType(lt);
        setLeaveTypeForm({
            name: lt.name,
            code: lt.code,
            description: lt.description || '',
            days_allowed: lt.days_allowed,
            carry_forward: lt.carry_forward,
            max_carry_forward_days: lt.max_carry_forward_days || 0,
            is_paid: lt.is_paid,
            is_active: lt.is_active,
            pro_rata: lt.pro_rata || false,
            min_tenure_months: lt.min_tenure_months || 0
        });
        setIsLeaveTypeOpen(true);
    };

    const handleCreateOrUpdateLeaveType = async (e) => {
        e.preventDefault();
        setCreateLoading(true);
        try {
            if (editingLeaveType) {
                await leaveAPI.updateType(editingLeaveType.id, leaveTypeForm);
                toast.success('Leave type updated');
            } else {
                await leaveAPI.createType({ ...leaveTypeForm, company_id: company.id }); // Use company.id from state
                toast.success('Leave type created');
            }
            setIsLeaveTypeOpen(false);
            setEditingLeaveType(null);
            setLeaveTypeForm({
                name: '', code: '', description: '', days_allowed: 0,
                carry_forward: false, max_carry_forward_days: 0, is_paid: true, is_active: true, pro_rata: false, min_tenure_months: 0
            });
            // Refresh
            const res = await leaveAPI.getTypes({ is_active: null });
            setLeaveTypes(res.data);
        } catch (error) {
            const detail = error.response?.data?.detail;
            const msg = typeof detail === 'string' ? detail :
                Array.isArray(detail) ? detail.map(e => e.msg).join(', ') : 'Failed to save leave type';
            toast.error(msg);
        } finally {
            setCreateLoading(false);
        }
    };

    const toggleLeaveTypeStatus = async (lt) => {
        const action = lt.is_active ? 'deactivate' : 'activate';
        const confirmed = await confirm({
            title: `${lt.is_active ? 'Deactivate' : 'Activate'} Leave Type`,
            description: `Are you sure you want to ${action} this leave type?`,
            confirmText: lt.is_active ? 'Deactivate' : 'Activate',
            variant: lt.is_active ? 'destructive' : 'default',
        });
        if (!confirmed) return;
        try {
            await leaveAPI.updateType(lt.id, { is_active: !lt.is_active });
            toast.success('Leave type status updated');
            const res = await leaveAPI.getTypes({ is_active: null });
            setLeaveTypes(res.data);
        } catch (error) {
            toast.error('Failed to update status');
        }
    };

    const handleDeleteLeaveType = async (lt) => {
        const confirmed = await confirm({
            title: 'Delete Leave Type',
            description: `Are you sure you want to permanently delete "${lt.name}"? This cannot be undone. Leave types with existing requests cannot be deleted.`,
            confirmText: 'Delete',
            variant: 'destructive',
        });
        if (!confirmed) return;
        try {
            await leaveAPI.deleteType(lt.id);
            toast.success('Leave type deleted');
            const res = await leaveAPI.getTypes({ is_active: null });
            setLeaveTypes(res.data);
        } catch (error) {
            const detail = error.response?.data?.detail;
            toast.error(detail || 'Failed to delete leave type');
        }
    };

    const handleSaveLeaveSettings = async () => {
        setSavingLeaveSettings(true);
        try {
            await companyAPI.updateCurrent({ 
                leave_refresh_cycle: leaveRefreshCycle,
                allowed_optional_holidays: parseInt(allowedOptionalHolidays) || 0
            });
            toast.success('Leave settings saved');
        } catch {
            toast.error('Failed to save leave settings');
        } finally {
            setSavingLeaveSettings(false);
        }
    };

    // Asset Category Handlers
    const handleEditAssetCat = (cat) => {
        setEditingAssetCat(cat);
        setAssetCatForm({ name: cat.name, code: cat.code || '', description: cat.description || '', icon: cat.icon || '' });
        setIsAssetCatOpen(true);
    };

    const handleCreateOrUpdateAssetCat = async (e) => {
        e.preventDefault();
        setCreateLoading(true);
        try {
            if (editingAssetCat) {
                await settingsAPI.updateAssetCategory(editingAssetCat.id, assetCatForm);
                toast.success('Asset category updated');
            } else {
                await settingsAPI.createAssetCategory(assetCatForm);
                toast.success('Asset category created');
            }
            setIsAssetCatOpen(false);
            setEditingAssetCat(null);
            setAssetCatForm({ name: '', code: '', description: '', icon: '' });
            const res = await settingsAPI.getAssetCategories();
            if (Array.isArray(res.data)) {
                setAssetCategories(res.data.filter(cat => typeof cat.name === 'string'));
            }
        } catch (error) {
            const detail = error.response?.data?.detail;
            const message = typeof detail === 'string' ? detail :
                Array.isArray(detail) ? detail.map(e => e.msg).join(', ') : 'Failed to save asset category';
            toast.error(message);
        } finally {
            setCreateLoading(false);
        }
    };

    const handleDeleteAssetCat = async (id) => {
        const confirmed = await confirm({
            title: 'Delete Asset Category',
            description: 'Are you sure? This will fail if any assets are using this category.',
            confirmText: 'Delete',
            variant: 'destructive',
        });
        if (!confirmed) return;
        try {
            await settingsAPI.deleteAssetCategory(id);
            toast.success('Asset category deleted');
            setAssetCategories(assetCategories.filter(c => c.id !== id));
        } catch (error) {
            const detail = error.response?.data?.detail;
            const message = typeof detail === 'string' ? detail :
                Array.isArray(detail) ? detail.map(e => e.msg).join(', ') : 'Failed to delete asset category';
            toast.error(message);
        }
    };

    // PF Configuration Handlers
    const applyPfPreset = (presetName) => {
        let slabs = [];
        if (presetName === 'statutory') {
            slabs = [
                { min_salary: 0, max_salary: 25000, calc_type: 'percentage', value: 12, cap: 3000, formula_expr: '' },
                { min_salary: 25000.01, max_salary: null, calc_type: 'flat', value: 3000, cap: null, formula_expr: '' },
            ];
        } else if (presetName === 'uncapped') {
            slabs = [
                { min_salary: 0, max_salary: 25000, calc_type: 'percentage', value: 12, cap: 3000, formula_expr: '' },
                { min_salary: 25000.01, max_salary: null, calc_type: 'percentage', value: 12, cap: null, formula_expr: '' },
            ];
        } else if (presetName === 'exempt') {
            slabs = [
                { min_salary: 0, max_salary: 25000, calc_type: 'percentage', value: 12, cap: 3000, formula_expr: '' },
                { min_salary: 25000.01, max_salary: null, calc_type: 'flat', value: 0, cap: null, formula_expr: '' },
            ];
        }
        setFormData((prev) => ({
            ...prev,
            pf_contribution: {
                ...(prev.pf_contribution || {}),
                type: 'slabs',
                slabs,
            },
        }));
        toast.success(`Applied ${presetName} PF preset`);
    };

    const addPfSlab = (isEmployer = false) => {
        const fieldName = isEmployer ? 'employer_slabs' : 'slabs';
        const currentSlabs = formData.pf_contribution?.[fieldName] || [];
        const lastSlab = currentSlabs[currentSlabs.length - 1];
        const nextMin = lastSlab && lastSlab.max_salary !== null && lastSlab.max_salary !== undefined && lastSlab.max_salary !== ''
            ? Number(lastSlab.max_salary) + 1
            : 0;
        const newSlab = {
            min_salary: nextMin,
            max_salary: null,
            calc_type: 'percentage',
            value: 12,
            cap: 3000,
            formula_expr: '',
        };
        setFormData((prev) => ({
            ...prev,
            pf_contribution: {
                ...(prev.pf_contribution || {}),
                [fieldName]: [...currentSlabs, newSlab],
            },
        }));
    };

    const updatePfSlab = (index, key, value, isEmployer = false) => {
        const fieldName = isEmployer ? 'employer_slabs' : 'slabs';
        const currentSlabs = [...(formData.pf_contribution?.[fieldName] || [])];
        if (currentSlabs[index]) {
            currentSlabs[index] = { ...currentSlabs[index], [key]: value };
            setFormData((prev) => ({
                ...prev,
                pf_contribution: {
                    ...(prev.pf_contribution || {}),
                    [fieldName]: currentSlabs,
                },
            }));
        }
    };

    const removePfSlab = (index, isEmployer = false) => {
        const fieldName = isEmployer ? 'employer_slabs' : 'slabs';
        const currentSlabs = [...(formData.pf_contribution?.[fieldName] || [])];
        currentSlabs.splice(index, 1);
        setFormData((prev) => ({
            ...prev,
            pf_contribution: {
                ...(prev.pf_contribution || {}),
                [fieldName]: currentSlabs,
            },
        }));
    };

    const handleResolveCoords = async () => {
        if (!mapsUrl) return;
        
        // Client-side regex patterns
        const atPattern = /@(-?\d+\.\d+),(-?\d+\.\d+)/;
        const qPattern = /[?&]q=(-?\d+\.\d+),(-?\d+\.\d+)/;
        const llPattern = /[?&]ll=(-?\d+\.\d+),(-?\d+\.\d+)/;
        
        let match = mapsUrl.match(atPattern) || mapsUrl.match(qPattern) || mapsUrl.match(llPattern);
        if (match) {
            setLocForm(prev => ({
                ...prev,
                latitude: match[1],
                longitude: match[2]
            }));
            toast.success("Coordinates parsed from URL!");
            return;
        }
        
        // If no coordinates in the URL, try backend resolution (e.g. for shortened links)
        setResolvingCoords(true);
        try {
            const response = await settingsAPI.resolveCoords(mapsUrl);
            if (response.data && response.data.latitude && response.data.longitude) {
                setLocForm(prev => ({
                    ...prev,
                    latitude: response.data.latitude.toString(),
                    longitude: response.data.longitude.toString()
                }));
                toast.success("Coordinates resolved from shortened link!");
            } else {
                toast.error("Could not extract coordinates from the link.");
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || "Failed to resolve coordinates.");
        } finally {
            setResolvingCoords(false);
        }
    };

    // Location Handlers
    const handleEditLoc = (loc) => {
        setEditingLoc(loc);
        setLocForm({
            name: loc.name,
            description: loc.description || '',
            latitude: loc.latitude,
            longitude: loc.longitude,
            radius: loc.radius || 100
        });
        setIsLocOpen(true);
    };

    const handleCreateOrUpdateLoc = async (e) => {
        e.preventDefault();
        setCreateLoading(true);
        try {
            const payload = {
                ...locForm,
                latitude: parseFloat(locForm.latitude),
                longitude: parseFloat(locForm.longitude),
                radius: parseFloat(locForm.radius) || 100
            };

            if (editingLoc) {
                await settingsAPI.updateLocation(editingLoc.id, payload);
                toast.success('Location updated');
            } else {
                await settingsAPI.createLocation(payload);
                toast.success('Location created');
            }
            setIsLocOpen(false);
            setEditingLoc(null);
            setLocForm({ name: '', description: '', latitude: '', longitude: '', radius: 100 });
            const res = await settingsAPI.getLocations();
            setLocations(res.data);
        } catch (error) {
            const detail = error.response?.data?.detail;
            const message = typeof detail === 'string' ? detail :
                Array.isArray(detail) ? detail.map(e => e.msg).join(', ') : 'Failed to save location';
            toast.error(message);
        } finally {
            setCreateLoading(false);
        }
    };

    const handleDeleteLoc = async (id) => {
        const confirmed = await confirm({
            title: 'Delete Location',
            description: 'Are you sure you want to delete this location? This might affect attendance geo-fencing.',
            confirmText: 'Delete',
            variant: 'destructive',
        });
        if (!confirmed) return;
        try {
            await settingsAPI.deleteLocation(id);
            toast.success('Location deleted');
            setLocations(locations.filter(l => l.id !== id));
        } catch (error) {
            toast.error('Failed to delete location');
        }
    };

    const handleSaveAttendanceConfig = async () => {
        setSavingAttendance(true);
        try {
            // Ensure company_id is present
            const payload = {
                ...attendanceConfig,
                company_id: company.id
            };
            await attendanceAPI.updateConfig(payload);
            toast.success('Attendance configuration saved');
        } catch (error) {
            console.error('Save Error Details:', error);
            const detail = error.response?.data?.detail;
            const message = typeof detail === 'string' ? detail : 
                          (Array.isArray(detail) ? detail.map(d => d.msg).join(', ') : 'Failed to save attendance configuration');
            toast.error(message);
        } finally {
            setSavingAttendance(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 className="h-8 w-8 animate-spin" />
            </div>
        );
    }

    if (error) {
        return <ModuleDisabled moduleName="settings" />;
    }

    const menuCategories = [
        {
            title: 'Company Setup',
            items: [
                { id: 'company', label: 'Company Profile', icon: Building2 },
                { id: 'bank', label: 'Bank Details', icon: HardDrive },
                { id: 'modules', label: 'Enabled Modules', icon: Package },
            ]
        },
        {
            title: 'Statutory & Finance',
            items: [
                { id: 'statutory', label: 'Statutory Details', icon: ShieldCheck },
                { id: 'payroll', label: 'Payroll Config', icon: DollarSign },
            ]
        },
        {
            title: 'Core HR Setup',
            items: [
                { id: 'departments', label: 'Departments', icon: Users },
                { id: 'designations', label: 'Designations', icon: Briefcase },
                { id: 'leavetypes', label: 'Leave Types', icon: Calendar },
            ]
        },
        {
            title: 'Time & Operations',
            items: [
                { id: 'attendance', label: 'Attendance Rules', icon: Clock },
                { id: 'locations', label: 'Locations & GPS', icon: MapPin },
                { id: 'kiosk', label: 'Kiosk / FRA', icon: Smartphone },
            ]
        },
        {
            title: 'Preferences',
            items: [
                { id: 'notifications', label: 'Notifications', icon: Bell },
                { id: 'assetcategories', label: 'Asset Categories', icon: Monitor },
            ]
        }
    ];

    const SidebarLink = ({ id, label, icon: Icon }) => (
        <button
            onClick={() => setActiveSection(id)}
            className={`w-full text-left px-3 py-2 rounded-lg text-sm font-semibold flex items-center gap-2.5 transition-all ${
                activeSection === id
                    ? 'bg-amber-400 text-black shadow-sm font-bold'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
            }`}
        >
            <Icon className={`h-4 w-4 shrink-0 transition-colors ${activeSection === id ? 'text-black' : 'text-slate-400 group-hover:text-slate-600'}`} />
            <span>{label}</span>
        </button>
    );

    const SaveBar = ({ title, description }) => (
        <div className="flex items-start justify-between pb-4 border-b mb-6">
            <div>
                <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
                <p className="text-sm text-muted-foreground mt-0.5">{description}</p>
            </div>
            <Button onClick={handleSaveGeneral} disabled={saving} size="sm">
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                Save Changes
            </Button>
        </div>
    );

    return (
        <div className="space-y-6 animate-slide-up">
            <div>
                <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
                <p className="text-muted-foreground">Manage your company configuration</p>
            </div>

            <div className="flex gap-8 items-start">
                {/* Sidebar */}
                <aside className="w-60 shrink-0 sticky top-4 bg-slate-50/50 border rounded-xl p-3.5 shadow-sm">
                    <nav className="space-y-4">
                        {menuCategories.map((category) => (
                            <div key={category.title} className="space-y-1">
                                <p className="text-[10px] font-black uppercase tracking-wider text-slate-400/80 mb-1.5 px-2.5">
                                    {category.title}
                                </p>
                                <div className="space-y-0.5">
                                    {category.items.map(item => (
                                        <SidebarLink key={item.id} {...item} />
                                    ))}
                                </div>
                            </div>
                        ))}
                    </nav>
                </aside>

                {/* Content */}
                <main className="flex-1 min-w-0">

                {/* ── Company Info ── */}
                {activeSection === 'company' && (
                    <div>
                        <SaveBar title="Company Information" description="Basic details and contact information" />
                        <Card>
                            <CardContent className="pt-6 space-y-4">
                                <div className="flex items-center gap-4 pb-2">
                                    <div className="h-20 w-20 rounded-lg border bg-muted flex items-center justify-center overflow-hidden">
                                        {formData.logo
                                            ? <img src={`${process.env.REACT_APP_BACKEND_URL}${formData.logo}`} alt="Logo" className="h-full w-full object-contain" />
                                            : <Building2 className="h-8 w-8 text-muted-foreground" />
                                        }
                                    </div>
                                    <div className="space-y-1">
                                        <Label htmlFor="logo" className="cursor-pointer text-sm font-medium text-primary hover:underline">Upload Logo</Label>
                                        <Input id="logo" type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
                                        <p className="text-xs text-muted-foreground">Square PNG/JPG, max 2MB</p>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label>Company Name</Label>
                                    <Input value={formData.name || ''} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Email</Label>
                                        <Input type="email" value={formData.email || ''} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Phone</Label>
                                        <Input value={formData.phone || ''} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label>Address</Label>
                                    <Input value={formData.address || ''} onChange={(e) => setFormData({ ...formData, address: e.target.value })} />
                                </div>
                                <div className="grid grid-cols-3 gap-4">
                                    <div className="space-y-2">
                                        <Label>City</Label>
                                        <Input value={formData.city || ''} onChange={(e) => setFormData({ ...formData, city: e.target.value })} />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>State</Label>
                                        <Input value={formData.state || ''} onChange={(e) => setFormData({ ...formData, state: e.target.value })} />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Pincode</Label>
                                        <Input value={formData.pincode || ''} onChange={(e) => setFormData({ ...formData, pincode: e.target.value })} />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                )}

                {/* ── Bank Details ── */}
                {activeSection === 'bank' && (
                    <div>
                        <SaveBar title="Bank Details" description="Company current account used for payroll disbursements" />
                        <Card>
                            <CardContent className="pt-6 space-y-4">
                                <div className="space-y-2">
                                    <Label>Bank Name</Label>
                                    <Input value={formData.bank_name || ''} onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })} placeholder="e.g. HDFC Bank" />
                                </div>
                                <div className="space-y-2">
                                    <Label>Account Number</Label>
                                    <Input value={formData.bank_account_number || ''} onChange={(e) => setFormData({ ...formData, bank_account_number: e.target.value })} />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>IFSC Code</Label>
                                        <Input value={formData.bank_ifsc || ''} onChange={(e) => setFormData({ ...formData, bank_ifsc: e.target.value })} />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Branch</Label>
                                        <Input value={formData.bank_branch || ''} onChange={(e) => setFormData({ ...formData, bank_branch: e.target.value })} />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                )}

                {/* ── Statutory ── */}
                {activeSection === 'statutory' && (
                    <div>
                        <SaveBar title="Statutory Details" description="PF, ESI, TAN, PAN and Professional Tax configuration" />
                        <Card>
                            <CardContent className="pt-6 space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>PF Number</Label>
                                        <Input value={formData.pf_number || ''} onChange={(e) => setFormData({ ...formData, pf_number: e.target.value })} />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>ESI Number</Label>
                                        <Input value={formData.esi_number || ''} onChange={(e) => setFormData({ ...formData, esi_number: e.target.value })} />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>TAN</Label>
                                        <Input value={formData.tan || ''} onChange={(e) => setFormData({ ...formData, tan: e.target.value })} />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>PAN</Label>
                                        <Input value={formData.pan || ''} onChange={(e) => setFormData({ ...formData, pan: e.target.value })} />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label>Professional Tax State</Label>
                                    <Select value={formData.pt_state || ''} onValueChange={(v) => setFormData({ ...formData, pt_state: v })}>
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select state for PT calculation" />
                                        </SelectTrigger>
                                        <SelectContent className="max-h-[200px]">
                                            {ptStates && ptStates.length > 0 ? (
                                                ptStates.map(cfg => (
                                                    <SelectItem key={cfg.id} value={cfg.state_name}>{cfg.state_name}</SelectItem>
                                                ))
                                            ) : (
                                                <>
                                                    <SelectItem value="Andhra Pradesh">Andhra Pradesh</SelectItem>
                                                    <SelectItem value="Assam">Assam</SelectItem>
                                                    <SelectItem value="Bihar">Bihar</SelectItem>
                                                    <SelectItem value="Chhattisgarh">Chhattisgarh</SelectItem>
                                                    <SelectItem value="Delhi">Delhi</SelectItem>
                                                    <SelectItem value="Goa">Goa</SelectItem>
                                                    <SelectItem value="Gujarat">Gujarat</SelectItem>
                                                    <SelectItem value="Haryana">Haryana</SelectItem>
                                                    <SelectItem value="Himachal Pradesh">Himachal Pradesh</SelectItem>
                                                    <SelectItem value="Jharkhand">Jharkhand</SelectItem>
                                                    <SelectItem value="Karnataka">Karnataka</SelectItem>
                                                    <SelectItem value="Kerala">Kerala</SelectItem>
                                                    <SelectItem value="Madhya Pradesh">Madhya Pradesh</SelectItem>
                                                    <SelectItem value="Maharashtra">Maharashtra</SelectItem>
                                                    <SelectItem value="Manipur">Manipur</SelectItem>
                                                    <SelectItem value="Meghalaya">Meghalaya</SelectItem>
                                                    <SelectItem value="Mizoram">Mizoram</SelectItem>
                                                    <SelectItem value="Nagaland">Nagaland</SelectItem>
                                                    <SelectItem value="Odisha">Odisha</SelectItem>
                                                    <SelectItem value="Punjab">Punjab</SelectItem>
                                                    <SelectItem value="Rajasthan">Rajasthan</SelectItem>
                                                    <SelectItem value="Sikkim">Sikkim</SelectItem>
                                                    <SelectItem value="Tamil Nadu">Tamil Nadu</SelectItem>
                                                    <SelectItem value="Telangana">Telangana</SelectItem>
                                                    <SelectItem value="Tripura">Tripura</SelectItem>
                                                    <SelectItem value="Uttar Pradesh">Uttar Pradesh</SelectItem>
                                                    <SelectItem value="Uttarakhand">Uttarakhand</SelectItem>
                                                    <SelectItem value="West Bengal">West Bengal</SelectItem>
                                                </>
                                            )}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                )}

                {/* ── Payroll Config ── */}
                {activeSection === 'payroll' && (
                    <div className="space-y-4">
                        <SaveBar title="Payroll Configuration" description="Configure how payroll components are calculated" />
                        <Card>
                            <CardHeader>
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                    <div>
                                        <CardTitle className="text-base">PF Contribution</CardTitle>
                                        <CardDescription>Configure how employee and employer Provident Fund contributions are computed each month</CardDescription>
                                    </div>
                                    {formData.pf_contribution?.type === 'slabs' && (
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-xs font-medium text-muted-foreground">Presets:</span>
                                            <Button type="button" variant="outline" size="sm" onClick={() => applyPfPreset('statutory')}>
                                                Standard EPFO (₹3,000 Cap)
                                            </Button>
                                            <Button type="button" variant="outline" size="sm" onClick={() => applyPfPreset('uncapped')}>
                                                Uncapped High Earners
                                            </Button>
                                            <Button type="button" variant="outline" size="sm" onClick={() => applyPfPreset('exempt')}>
                                                High Earner Exempt (&gt; ₹25k)
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                <div className="space-y-2">
                                    <Label>Contribution Method</Label>
                                    <Select
                                        value={formData.pf_contribution?.type || 'percentage'}
                                        onValueChange={(v) => {
                                            const updated = { ...(formData.pf_contribution || {}), type: v };
                                            if (v === 'slabs' && (!updated.slabs || updated.slabs.length === 0)) {
                                                updated.slabs = [
                                                    { min_salary: 0, max_salary: 25000, calc_type: 'percentage', value: 12, cap: 3000, formula_expr: '' },
                                                    { min_salary: 25000.01, max_salary: null, calc_type: 'flat', value: 3000, cap: null, formula_expr: '' },
                                                ];
                                            }
                                            setFormData({ ...formData, pf_contribution: updated });
                                        }}
                                    >
                                        <SelectTrigger className="max-w-md">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="percentage">Percentage of Basic Pay (Legacy / Standard)</SelectItem>
                                            <SelectItem value="flat">Flat Amount per Month</SelectItem>
                                            <SelectItem value="slabs">Slab-Based / Tiered Rules (Custom Formulas by Salary)</SelectItem>
                                            <SelectItem value="formula">Custom Mathematical Formula Expression</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                {/* Mode 1: Percentage */}
                                {formData.pf_contribution?.type === 'percentage' && (
                                    <div className="space-y-2">
                                        <Label>Rate (%)</Label>
                                        <Input
                                            type="number"
                                            min={0}
                                            className="max-w-xs"
                                            value={formData.pf_contribution?.value ?? 12}
                                            onChange={(e) => setFormData({ ...formData, pf_contribution: { ...formData.pf_contribution, value: parseFloat(e.target.value) || 0 } })}
                                        />
                                        <p className="text-xs text-muted-foreground">
                                            Applied on Basic + DA. Capped at ₹3,000/month when Basic + DA ≥ ₹25,000.
                                        </p>
                                    </div>
                                )}

                                {/* Mode 2: Flat Amount */}
                                {formData.pf_contribution?.type === 'flat' && (
                                    <div className="space-y-2">
                                        <Label>Flat Amount (₹/month)</Label>
                                        <Input
                                            type="number"
                                            min={0}
                                            className="max-w-xs"
                                            value={formData.pf_contribution?.value ?? 3000}
                                            onChange={(e) => setFormData({ ...formData, pf_contribution: { ...formData.pf_contribution, value: parseFloat(e.target.value) || 0 } })}
                                        />
                                        <p className="text-xs text-muted-foreground">
                                            Fixed deduction per month, pro-rated automatically for LOP days.
                                        </p>
                                    </div>
                                )}

                                {/* Mode 3: Slabs */}
                                {formData.pf_contribution?.type === 'slabs' && (
                                    <div className="space-y-4 border rounded-lg p-4 bg-muted/20">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <h4 className="text-sm font-semibold">Employee PF Slabs</h4>
                                                <p className="text-xs text-muted-foreground">Define different calculation formulas based on the employee's monthly basic pay + dearness allowance (DA)</p>
                                            </div>
                                            <Button type="button" size="sm" variant="outline" onClick={() => addPfSlab(false)}>
                                                <Plus className="h-4 w-4 mr-1" /> Add Slab
                                            </Button>
                                        </div>

                                        <div className="overflow-x-auto border rounded-md bg-background">
                                            <Table>
                                                <TableHeader>
                                                    <TableRow>
                                                        <TableHead className="w-28">Min Wage (₹)</TableHead>
                                                        <TableHead className="w-28">Max Wage (₹)</TableHead>
                                                        <TableHead className="w-36">Method</TableHead>
                                                        <TableHead className="w-32">Rate / Amount</TableHead>
                                                        <TableHead className="w-32">Cap (₹, Opt)</TableHead>
                                                        <TableHead className="w-16 text-center">Action</TableHead>
                                                    </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                    {(formData.pf_contribution?.slabs || []).map((slab, idx) => (
                                                        <TableRow key={idx}>
                                                            <TableCell>
                                                                <Input
                                                                    type="number"
                                                                    min={0}
                                                                    value={slab.min_salary ?? 0}
                                                                    onChange={(e) => updatePfSlab(idx, 'min_salary', parseFloat(e.target.value) || 0, false)}
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                <Input
                                                                    type="number"
                                                                    placeholder="No limit"
                                                                    value={slab.max_salary ?? ''}
                                                                    onChange={(e) => updatePfSlab(idx, 'max_salary', e.target.value === '' ? null : parseFloat(e.target.value) || 0, false)}
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                <Select
                                                                    value={slab.calc_type || 'percentage'}
                                                                    onValueChange={(v) => updatePfSlab(idx, 'calc_type', v, false)}
                                                                >
                                                                    <SelectTrigger>
                                                                        <SelectValue />
                                                                    </SelectTrigger>
                                                                    <SelectContent>
                                                                        <SelectItem value="percentage">Percentage</SelectItem>
                                                                        <SelectItem value="flat">Flat Amount</SelectItem>
                                                                        <SelectItem value="formula">Expression</SelectItem>
                                                                    </SelectContent>
                                                                </Select>
                                                            </TableCell>
                                                            <TableCell>
                                                                {slab.calc_type === 'formula' ? (
                                                                    <Input
                                                                        placeholder="e.g. min((basic + da)*0.12, 3000)"
                                                                        value={slab.formula_expr || ''}
                                                                        onChange={(e) => updatePfSlab(idx, 'formula_expr', e.target.value, false)}
                                                                    />
                                                                ) : (
                                                                    <Input
                                                                        type="number"
                                                                        min={0}
                                                                        placeholder={slab.calc_type === 'flat' ? '₹' : '%'}
                                                                        value={slab.value ?? ''}
                                                                        onChange={(e) => updatePfSlab(idx, 'value', parseFloat(e.target.value) || 0, false)}
                                                                    />
                                                                )}
                                                            </TableCell>
                                                            <TableCell>
                                                                {slab.calc_type === 'percentage' ? (
                                                                    <Input
                                                                        type="number"
                                                                        min={0}
                                                                        placeholder="No Cap"
                                                                        value={slab.cap ?? ''}
                                                                        onChange={(e) => updatePfSlab(idx, 'cap', e.target.value === '' ? null : parseFloat(e.target.value) || 0, false)}
                                                                    />
                                                                ) : (
                                                                    <span className="text-xs text-muted-foreground pl-2">-</span>
                                                                )}
                                                            </TableCell>
                                                            <TableCell className="text-center">
                                                                <Button
                                                                    type="button"
                                                                    variant="ghost"
                                                                    size="icon"
                                                                    className="h-8 w-8 text-destructive hover:text-destructive"
                                                                    onClick={() => removePfSlab(idx, false)}
                                                                    disabled={(formData.pf_contribution?.slabs || []).length <= 1}
                                                                >
                                                                    <Trash2 className="h-4 w-4" />
                                                                </Button>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </div>
                                    </div>
                                )}

                                {/* Mode 4: Custom Formula Expression */}
                                {formData.pf_contribution?.type === 'formula' && (
                                    <div className="space-y-2 border rounded-lg p-4 bg-muted/20">
                                        <Label>Custom Formula Expression</Label>
                                        <Input
                                            placeholder="e.g. min((basic + da) * 0.12, 3000)"
                                            className="max-w-md font-mono text-sm"
                                            value={formData.pf_contribution?.formula_expr || ''}
                                            onChange={(e) => setFormData({
                                                ...formData,
                                                pf_contribution: { ...formData.pf_contribution, formula_expr: e.target.value }
                                            })}
                                        />
                                        <p className="text-xs text-muted-foreground">
                                            Supported variables: <code className="font-mono text-xs">wage</code> (basic + da), <code className="font-mono text-xs">basic</code>, <code className="font-mono text-xs">da</code>, <code className="font-mono text-xs">monthly_wage</code>, <code className="font-mono text-xs">monthly_basic</code>, <code className="font-mono text-xs">monthly_da</code>, <code className="font-mono text-xs">paid_days</code>, <code className="font-mono text-xs">divisor</code> (30.45). Supported functions: <code className="font-mono text-xs">min</code>, <code className="font-mono text-xs">max</code>, <code className="font-mono text-xs">round</code>.
                                        </p>
                                    </div>
                                )}

                                {/* Employer Contribution Settings */}
                                <div className="pt-2 border-t space-y-4">
                                    <div className="flex items-start space-x-3">
                                        <Checkbox
                                            id="pf_employer_matches_employee"
                                            className="mt-0.5"
                                            checked={formData.pf_contribution?.employer_matches_employee ?? true}
                                            onCheckedChange={(c) => setFormData({
                                                ...formData,
                                                pf_contribution: {
                                                    ...formData.pf_contribution,
                                                    employer_matches_employee: c,
                                                    employer_slabs: c ? null : (formData.pf_contribution?.employer_slabs || formData.pf_contribution?.slabs || [])
                                                }
                                            })}
                                        />
                                        <div className="space-y-1">
                                            <Label htmlFor="pf_employer_matches_employee" className="cursor-pointer font-medium">Employer PF matches Employee PF (Standard)</Label>
                                            <p className="text-xs text-muted-foreground leading-normal">
                                                When enabled, the employer contribution is equal to the employee contribution. Uncheck to configure independent employer contribution slabs or caps.
                                            </p>
                                        </div>
                                    </div>

                                    {/* Independent Employer Slabs if unchecked */}
                                    {formData.pf_contribution?.employer_matches_employee === false && (
                                        <div className="space-y-4 border rounded-lg p-4 bg-muted/20 ml-6">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <h4 className="text-sm font-semibold">Employer PF Slabs</h4>
                                                    <p className="text-xs text-muted-foreground">Independent rules for Employer contribution (e.g. capped at ₹3,000 even if employee contributes uncapped)</p>
                                                </div>
                                                <Button type="button" size="sm" variant="outline" onClick={() => addPfSlab(true)}>
                                                    <Plus className="h-4 w-4 mr-1" /> Add Slab
                                                </Button>
                                            </div>

                                            <div className="overflow-x-auto border rounded-md bg-background">
                                                <Table>
                                                    <TableHeader>
                                                        <TableRow>
                                                            <TableHead className="w-28">Min Wage (₹)</TableHead>
                                                            <TableHead className="w-28">Max Wage (₹)</TableHead>
                                                            <TableHead className="w-36">Method</TableHead>
                                                            <TableHead className="w-32">Rate / Amount</TableHead>
                                                            <TableHead className="w-32">Cap (₹, Opt)</TableHead>
                                                            <TableHead className="w-16 text-center">Action</TableHead>
                                                        </TableRow>
                                                    </TableHeader>
                                                    <TableBody>
                                                        {(formData.pf_contribution?.employer_slabs || []).map((slab, idx) => (
                                                            <TableRow key={idx}>
                                                                <TableCell>
                                                                    <Input
                                                                        type="number"
                                                                        min={0}
                                                                        value={slab.min_salary ?? 0}
                                                                        onChange={(e) => updatePfSlab(idx, 'min_salary', parseFloat(e.target.value) || 0, true)}
                                                                    />
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Input
                                                                        type="number"
                                                                        placeholder="No limit"
                                                                        value={slab.max_salary ?? ''}
                                                                        onChange={(e) => updatePfSlab(idx, 'max_salary', e.target.value === '' ? null : parseFloat(e.target.value) || 0, true)}
                                                                    />
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Select
                                                                        value={slab.calc_type || 'percentage'}
                                                                        onValueChange={(v) => updatePfSlab(idx, 'calc_type', v, true)}
                                                                    >
                                                                        <SelectTrigger>
                                                                            <SelectValue />
                                                                        </SelectTrigger>
                                                                        <SelectContent>
                                                                            <SelectItem value="percentage">Percentage</SelectItem>
                                                                            <SelectItem value="flat">Flat Amount</SelectItem>
                                                                        </SelectContent>
                                                                    </Select>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Input
                                                                        type="number"
                                                                        min={0}
                                                                        placeholder={slab.calc_type === 'flat' ? '₹' : '%'}
                                                                        value={slab.value ?? ''}
                                                                        onChange={(e) => updatePfSlab(idx, 'value', parseFloat(e.target.value) || 0, true)}
                                                                    />
                                                                </TableCell>
                                                                <TableCell>
                                                                    {slab.calc_type === 'percentage' ? (
                                                                        <Input
                                                                            type="number"
                                                                            min={0}
                                                                            placeholder="No Cap"
                                                                            value={slab.cap ?? ''}
                                                                            onChange={(e) => updatePfSlab(idx, 'cap', e.target.value === '' ? null : parseFloat(e.target.value) || 0, true)}
                                                                        />
                                                                    ) : (
                                                                        <span className="text-xs text-muted-foreground pl-2">-</span>
                                                                    )}
                                                                </TableCell>
                                                                <TableCell className="text-center">
                                                                    <Button
                                                                        type="button"
                                                                        variant="ghost"
                                                                        size="icon"
                                                                        className="h-8 w-8 text-destructive hover:text-destructive"
                                                                        onClick={() => removePfSlab(idx, true)}
                                                                        disabled={(formData.pf_contribution?.employer_slabs || []).length <= 1}
                                                                    >
                                                                        <Trash2 className="h-4 w-4" />
                                                                    </Button>
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Pro-rata Rules */}
                                <div className="pt-2 border-t space-y-3">
                                    <div className="flex items-start space-x-3">
                                        <Checkbox
                                            id="pf_pro_rate_on_joining"
                                            className="mt-0.5"
                                            checked={formData.pf_contribution?.pro_rate_on_joining ?? true}
                                            onCheckedChange={(c) => setFormData({
                                                ...formData,
                                                pf_contribution: {
                                                    ...formData.pf_contribution,
                                                    pro_rate_on_joining: c
                                                }
                                            })}
                                        />
                                        <div className="space-y-1">
                                            <Label htmlFor="pf_pro_rate_on_joining" className="cursor-pointer font-medium">Pro-rate PF on joining month</Label>
                                            <p className="text-xs text-muted-foreground leading-normal">
                                                If enabled, PF contribution will be calculated pro-rata based on the employee's date of joining in their first month.
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <Checkbox
                                            id="pf_pro_rate_on_lop"
                                            className="mt-0.5"
                                            checked={formData.pf_contribution?.pro_rate_on_lop ?? true}
                                            onCheckedChange={(c) => setFormData({
                                                ...formData,
                                                pf_contribution: {
                                                    ...formData.pf_contribution,
                                                    pro_rate_on_lop: c
                                                }
                                            })}
                                        />
                                        <div className="space-y-1">
                                            <Label htmlFor="pf_pro_rate_on_lop" className="cursor-pointer font-medium">Pro-rate statutory caps &amp; flat deductions for LOP days</Label>
                                            <p className="text-xs text-muted-foreground leading-normal">
                                                When unpaid leave (LOP) occurs, the statutory ₹3,000 cap and flat contribution scale down proportionally with paid days worked.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Live Simulator */}
                                <div className="border rounded-lg p-4 bg-muted/40 space-y-3">
                                    <div className="flex items-center gap-2">
                                        <Calculator className="h-4 w-4 text-primary" />
                                        <h4 className="text-sm font-semibold">Live PF Calculation Simulator</h4>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-5 gap-3 items-end">
                                        <div className="space-y-1">
                                            <Label className="text-xs">Test Monthly Basic (₹)</Label>
                                            <Input
                                                type="number"
                                                min={0}
                                                value={pfTestBasic}
                                                onChange={(e) => setPfTestBasic(parseFloat(e.target.value) || 0)}
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <Label className="text-xs">Test Monthly DA (₹)</Label>
                                            <Input
                                                type="number"
                                                min={0}
                                                value={pfTestDa}
                                                onChange={(e) => setPfTestDa(parseFloat(e.target.value) || 0)}
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <Label className="text-xs">Test LOP Days</Label>
                                            <Input
                                                type="number"
                                                min={0}
                                                max={31}
                                                value={pfTestLop}
                                                onChange={(e) => setPfTestLop(parseFloat(e.target.value) || 0)}
                                            />
                                        </div>
                                        {(() => {
                                            const sim = calculatePFEstimate(pfTestBasic, formData.pf_contribution, pfTestLop, pfTestDa);
                                            return (
                                                <>
                                                    <div className="p-2 border rounded bg-background text-center">
                                                        <div className="text-xs text-muted-foreground">Employee PF</div>
                                                        <div className="text-base font-bold text-primary">₹{sim.employeePF.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
                                                    </div>
                                                    <div className="p-2 border rounded bg-background text-center">
                                                        <div className="text-xs text-muted-foreground">Employer PF</div>
                                                        <div className="text-base font-bold text-primary">₹{sim.employerPF.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
                                                    </div>
                                                </>
                                            );
                                        })()}
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-base">Full &amp; Final Settlement</CardTitle>
                                <CardDescription>Configure what is included when processing a Full &amp; Final settlement</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="flex items-center space-x-2">
                                    <Checkbox
                                        id="fnf_leave_encashment"
                                        checked={formData.fnf_leave_encashment_enabled ?? true}
                                        onCheckedChange={(c) => setFormData({ ...formData, fnf_leave_encashment_enabled: c })}
                                    />
                                    <div>
                                        <Label htmlFor="fnf_leave_encashment">Include leave encashment</Label>
                                        <p className="text-xs text-muted-foreground mt-0.5">Encash earned / privilege leave balance during FnF using the formula: (Basic ÷ 30) × remaining days</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-base">Attendance-Based Deductions</CardTitle>
                                <CardDescription>Automatically deduct salary for absences, late check-ins, and early check-outs. Approved leaves and holidays are always excluded.</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="flex items-center space-x-2">
                                    <Checkbox
                                        id="att_payroll_enabled"
                                        checked={formData.attendance_payroll_config?.enabled ?? false}
                                        onCheckedChange={(c) => setFormData({ ...formData, attendance_payroll_config: { ...formData.attendance_payroll_config, enabled: c } })}
                                    />
                                    <div>
                                        <Label htmlFor="att_payroll_enabled">Enable attendance-based salary deduction</Label>
                                        <p className="text-xs text-muted-foreground mt-0.5">Absent days are always deducted. Per-day rate = gross ÷ 30.45.</p>
                                    </div>
                                </div>
                                {(formData.attendance_payroll_config?.enabled) && (
                                    <div className="ml-6 space-y-3 pt-3 border-t">
                                        <div className="flex items-center space-x-2">
                                            <Checkbox
                                                id="att_late_checkin"
                                                checked={formData.attendance_payroll_config?.late_checkin_is_halfday ?? true}
                                                onCheckedChange={(c) => setFormData({ ...formData, attendance_payroll_config: { ...formData.attendance_payroll_config, late_checkin_is_halfday: c } })}
                                            />
                                            <div>
                                                <Label htmlFor="att_late_checkin">Deduct half-day for late check-in</Label>
                                                <p className="text-xs text-muted-foreground mt-0.5">Applies when check-in is after the shift start + grace period.</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <Checkbox
                                                id="att_early_checkout"
                                                checked={formData.attendance_payroll_config?.early_checkout_is_halfday ?? true}
                                                onCheckedChange={(c) => setFormData({ ...formData, attendance_payroll_config: { ...formData.attendance_payroll_config, early_checkout_is_halfday: c } })}
                                            />
                                            <div>
                                                <Label htmlFor="att_early_checkout">Deduct half-day for early check-out</Label>
                                                <p className="text-xs text-muted-foreground mt-0.5">Late check-in + early check-out on the same day are capped at one full day.</p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                )}

                {/* ── Modules ── */}
                {activeSection === 'modules' && (
                    <div>
                        <div className="pb-4 border-b mb-6">
                            <h2 className="text-lg font-semibold text-slate-900">Modules</h2>
                            <p className="text-sm text-muted-foreground mt-0.5">Features enabled for your company — contact support to change</p>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            {company?.modules && Object.entries(company.modules).map(([key, value]) => (
                                <div key={key} className={`p-4 rounded-lg border ${value ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'}`}>
                                    <p className="font-medium text-sm capitalize text-slate-800">{key.replace(/_/g, ' ')}</p>
                                    <p className={`text-xs mt-0.5 ${value ? 'text-green-600' : 'text-slate-400'}`}>{value ? 'Enabled' : 'Disabled'}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* ── Notifications ── */}
                {activeSection === 'notifications' && (
                    <div>
                        <div className="flex items-start justify-between pb-4 border-b mb-6">
                            <div>
                                <h2 className="text-lg font-semibold text-slate-900">Notifications</h2>
                                <p className="text-sm text-muted-foreground mt-0.5">Configure when automated emails are sent to your team</p>
                            </div>
                            <Button onClick={handleSavePreferences} disabled={savingPreferences} size="sm">
                                {savingPreferences ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                                Save Changes
                            </Button>
                        </div>
                        <Card>
                            <CardHeader className="flex flex-row items-center gap-3">
                                <div className="p-2 rounded-lg bg-pink-50 text-pink-500">
                                    <Gift className="h-5 w-5" />
                                </div>
                                <div className="space-y-0.5">
                                    <CardTitle className="text-base">Celebration Email Notification</CardTitle>
                                    <CardDescription>
                                        Configure when daily birthday &amp; work anniversary digest is sent to roles with celebration emails enabled.
                                    </CardDescription>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="flex items-center justify-between p-4 border rounded-lg bg-slate-50/50">
                                    <div className="space-y-1 pr-4">
                                        <Label className="text-sm font-medium">Email Daily Celebrations</Label>
                                        <p className="text-xs text-muted-foreground">
                                            Send a digest of today's birthdays and work anniversaries to designated roles.
                                        </p>
                                    </div>
                                    <Switch
                                        checked={preferences?.celebration_email_enabled !== false}
                                        onCheckedChange={(checked) => setPreferences({ ...preferences, celebration_email_enabled: checked })}
                                    />
                                </div>
                                {preferences?.celebration_email_enabled !== false && (
                                    <div className="max-w-xs space-y-2 pt-2 animate-fade-in">
                                        <Label>Send at (IST)</Label>
                                        <Input
                                            type="time"
                                            value={celebrationTime}
                                            onChange={(e) => {
                                                const val = e.target.value || '00:00';
                                                setCelebrationTime(val);
                                                const [h] = val.split(':');
                                                setPreferences({ ...preferences, celebration_email_hour: Number(h) || 0 });
                                            }}
                                        />
                                    </div>
                                )}
                                {(window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') && (
                                    <div className="pt-4 border-t flex justify-end">
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={async () => {
                                                try {
                                                    await settingsAPI.testCelebrationEmail();
                                                    toast.success("Test celebration email triggered!");
                                                } catch (e) {
                                                    toast.error("Failed to trigger test email.");
                                                }
                                            }}
                                        >
                                            Send Test Celebration Email
                                        </Button>
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        <Card className="mt-6">
                            <CardHeader className="flex flex-row items-center gap-3">
                                <div className="p-2 rounded-lg bg-blue-50 text-blue-500">
                                    <CalendarX className="h-5 w-5" />
                                </div>
                                <div className="space-y-0.5">
                                    <CardTitle className="text-base">Absent Employee Notification</CardTitle>
                                    <CardDescription>
                                        Configure automated email alerts sent to employees when they are marked absent.
                                    </CardDescription>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="flex items-center justify-between p-4 border rounded-lg bg-slate-50/50">
                                    <div className="space-y-1 pr-4">
                                        <Label className="text-sm font-medium">Email Absent Employees</Label>
                                        <p className="text-xs text-muted-foreground">
                                            Automatically email employees if they missed marking their attendance for the day.
                                        </p>
                                    </div>
                                    <Switch
                                        checked={attendanceConfig?.enable_absent_email !== false}
                                        onCheckedChange={(checked) => setAttendanceConfig({ ...attendanceConfig, enable_absent_email: checked })}
                                    />
                                </div>
                                {attendanceConfig?.enable_absent_email !== false && (
                                    <div className="max-w-xs space-y-2 pt-2 animate-fade-in">
                                        <Label>Send at (IST)</Label>
                                        <Input
                                            type="time"
                                            value={attendanceConfig?.absent_email_time || '00:05'}
                                            onChange={(e) => setAttendanceConfig({ ...attendanceConfig, absent_email_time: e.target.value || '00:05' })}
                                        />
                                    </div>
                                )}
                                {(window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') && (
                                    <div className="pt-4 border-t flex justify-end">
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={async () => {
                                                try {
                                                    await settingsAPI.testAbsentEmail();
                                                    toast.success("Test absent email triggered!");
                                                } catch (e) {
                                                    toast.error("Failed to trigger test email.");
                                                }
                                            }}
                                        >
                                            Send Test Absent Email
                                        </Button>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                )}

                {/* ── Departments ── */}
                {activeSection === 'departments' && (
                    <div className="space-y-4">
                        <div className="flex items-start justify-between pb-4 border-b">
                            <div>
                                <h2 className="text-lg font-semibold text-slate-900">Departments</h2>
                                <p className="text-sm text-muted-foreground mt-0.5">Manage departments in your organization</p>
                            </div>
                            <Dialog open={isDeptOpen} onOpenChange={(open) => { setIsDeptOpen(open); if (!open) { setEditingDept(null); setDeptForm({ name: '', code: '' }); } }}>
                                <DialogTrigger asChild>
                                    <Button size="sm"><Plus className="mr-2 h-4 w-4" />Add Department</Button>
                                </DialogTrigger>
                                <DialogContent>
                                    <DialogHeader>
                                        <DialogTitle>{editingDept ? 'Edit Department' : 'Add Department'}</DialogTitle>
                                        <DialogDescription>{editingDept ? 'Update department details' : 'Create a new department'}</DialogDescription>
                                    </DialogHeader>
                                    <form onSubmit={handleCreateDept}>
                                        <div className="grid gap-4 py-4">
                                            <div className="space-y-2">
                                                <Label>Department Name *</Label>
                                                <Input value={deptForm.name} onChange={e => setDeptForm({ ...deptForm, name: e.target.value })} required placeholder="e.g. Engineering" />
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Department Code</Label>
                                                <Input value={deptForm.code} onChange={e => setDeptForm({ ...deptForm, code: e.target.value })} placeholder="e.g. ENG" />
                                            </div>
                                        </div>
                                        <DialogFooter>
                                            <Button type="button" variant="outline" onClick={() => setIsDeptOpen(false)}>Cancel</Button>
                                            <Button type="submit" disabled={createLoading}>{createLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{editingDept ? 'Update' : 'Create'}</Button>
                                        </DialogFooter>
                                    </form>
                                </DialogContent>
                            </Dialog>
                        </div>
                        <Card>
                            <CardContent className="p-0">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Name</TableHead>
                                            <TableHead>Code</TableHead>
                                            <TableHead className="w-[80px]">Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {departments.length === 0 ? (
                                            <TableRow><TableCell colSpan={3} className="text-center py-8 text-muted-foreground">No departments yet</TableCell></TableRow>
                                        ) : departments.map(dept => (
                                            <TableRow key={dept.id}>
                                                <TableCell className="font-medium">{dept.name}</TableCell>
                                                <TableCell>{dept.code || '—'}</TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-1">
                                                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => handleEditDept(dept)}><Edit className="h-4 w-4 text-slate-500" /></Button>
                                                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-destructive" onClick={() => handleDeleteDept(dept.id)}><Trash2 className="h-4 w-4" /></Button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    </div>
                )}

                {/* ── Designations ── */}
                {activeSection === 'designations' && (
                    <div className="space-y-4">
                        <div className="flex items-start justify-between pb-4 border-b">
                            <div>
                                <h2 className="text-lg font-semibold text-slate-900">Designations</h2>
                                <p className="text-sm text-muted-foreground mt-0.5">Manage job titles in your organization</p>
                            </div>
                            <Dialog open={isDesgOpen} onOpenChange={(open) => { setIsDesgOpen(open); if (!open) { setEditingDesg(null); setDesgForm({ name: '', department_id: '' }); } }}>
                                <DialogTrigger asChild>
                                    <Button size="sm"><Plus className="mr-2 h-4 w-4" />Add Designation</Button>
                                </DialogTrigger>
                                <DialogContent>
                                    <DialogHeader>
                                        <DialogTitle>{editingDesg ? 'Edit Designation' : 'Add Designation'}</DialogTitle>
                                        <DialogDescription>{editingDesg ? 'Update designation details' : 'Create a new job designation'}</DialogDescription>
                                    </DialogHeader>
                                    <form onSubmit={handleCreateDesg}>
                                        <div className="grid gap-4 py-4">
                                            <div className="space-y-2">
                                                <Label>Designation Name *</Label>
                                                <Input value={desgForm.name} onChange={e => setDesgForm({ ...desgForm, name: e.target.value })} required placeholder="e.g. Senior Software Engineer" />
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Associated Department</Label>
                                                <Select value={desgForm.department_id} onValueChange={v => setDesgForm({ ...desgForm, department_id: v })}>
                                                    <SelectTrigger><SelectValue placeholder="Select Department" /></SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="none">None</SelectItem>
                                                        {departments.map(dept => <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>)}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        </div>
                                        <DialogFooter>
                                            <Button type="button" variant="outline" onClick={() => setIsDesgOpen(false)}>Cancel</Button>
                                            <Button type="submit" disabled={createLoading}>{createLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{editingDesg ? 'Update' : 'Create'}</Button>
                                        </DialogFooter>
                                    </form>
                                </DialogContent>
                            </Dialog>
                        </div>
                        <Card>
                            <CardContent className="p-0">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Name</TableHead>
                                            <TableHead>Department</TableHead>
                                            <TableHead className="w-[80px]">Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {designations.length === 0 ? (
                                            <TableRow><TableCell colSpan={3} className="text-center py-8 text-muted-foreground">No designations yet</TableCell></TableRow>
                                        ) : designations.map(desg => (
                                            <TableRow key={desg.id}>
                                                <TableCell className="font-medium">{desg.name}</TableCell>
                                                <TableCell>{desg.department_name || '—'}</TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-1">
                                                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => handleEditDesg(desg)}><Edit className="h-4 w-4 text-slate-500" /></Button>
                                                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-destructive" onClick={() => handleDeleteDesg(desg.id)}><Trash2 className="h-4 w-4" /></Button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    </div>
                )}

                {/* ── Leave Types ── */}
                {activeSection === 'leavetypes' && (
                    <div className="space-y-4">
                        <div className="flex items-start justify-between pb-4 border-b">
                            <div>
                                <h2 className="text-lg font-semibold text-slate-900">Leave Types</h2>
                                <p className="text-sm text-muted-foreground mt-0.5">Configure leave policies for your organization</p>
                            </div>
                            <Dialog open={isLeaveTypeOpen} onOpenChange={(open) => { setIsLeaveTypeOpen(open); if (!open) { setEditingLeaveType(null); setLeaveTypeForm({ name: '', code: '', description: '', days_allowed: 0, carry_forward: false, max_carry_forward_days: 0, is_paid: true, is_active: true, pro_rata: false, min_tenure_months: 0 }); } }}>
                                <DialogTrigger asChild>
                                    <Button size="sm"><Plus className="mr-2 h-4 w-4" />Add Leave Type</Button>
                                </DialogTrigger>
                                <DialogContent className="max-w-xl">
                                    <DialogHeader><DialogTitle>{editingLeaveType ? 'Edit Leave Type' : 'Add Leave Type'}</DialogTitle></DialogHeader>
                                    <form onSubmit={handleCreateOrUpdateLeaveType}>
                                        <div className="grid gap-4 py-4">
                                            <div className="grid grid-cols-2 gap-4">
                                                <div className="space-y-2">
                                                    <Label>Name *</Label>
                                                    <Input value={leaveTypeForm.name} onChange={e => setLeaveTypeForm({ ...leaveTypeForm, name: e.target.value })} required />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label>Code *</Label>
                                                    <Input value={leaveTypeForm.code} onChange={e => setLeaveTypeForm({ ...leaveTypeForm, code: e.target.value })} required placeholder="e.g. CL, PL" disabled={!!editingLeaveType} />
                                                </div>
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Description</Label>
                                                <Input value={leaveTypeForm.description} onChange={e => setLeaveTypeForm({ ...leaveTypeForm, description: e.target.value })} />
                                            </div>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div className="space-y-2">
                                                    <Label>Days Allowed (Yearly) *</Label>
                                                    <Input type="number" value={leaveTypeForm.days_allowed} onChange={e => setLeaveTypeForm({ ...leaveTypeForm, days_allowed: parseFloat(e.target.value) })} required />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label>Max Carry Forward</Label>
                                                    <Input type="number" value={leaveTypeForm.max_carry_forward_days} onChange={e => setLeaveTypeForm({ ...leaveTypeForm, max_carry_forward_days: parseFloat(e.target.value) })} disabled={!leaveTypeForm.carry_forward} />
                                                </div>
                                            </div>
                                            <div className="flex gap-6 pt-2">
                                                <div className="flex items-center space-x-2">
                                                    <Checkbox id="carry_forward" checked={leaveTypeForm.carry_forward} onCheckedChange={(c) => setLeaveTypeForm({ ...leaveTypeForm, carry_forward: c })} />
                                                    <Label htmlFor="carry_forward">Carry Forward</Label>
                                                </div>
                                                <div className="flex items-center space-x-2">
                                                    <Checkbox id="is_paid" checked={leaveTypeForm.is_paid} onCheckedChange={(c) => setLeaveTypeForm({ ...leaveTypeForm, is_paid: c })} />
                                                    <Label htmlFor="is_paid">Paid Leave</Label>
                                                </div>
                                                <div className="flex items-center space-x-2">
                                                    <Checkbox id="is_active" checked={leaveTypeForm.is_active} onCheckedChange={(c) => setLeaveTypeForm({ ...leaveTypeForm, is_active: c })} />
                                                    <Label htmlFor="is_active">Active</Label>
                                                </div>
                                                <div className="flex items-center space-x-2">
                                                    <Checkbox id="pro_rata" checked={leaveTypeForm.pro_rata} onCheckedChange={(c) => setLeaveTypeForm({ ...leaveTypeForm, pro_rata: c })} />
                                                    <Label htmlFor="pro_rata">Pro-rata on joining</Label>
                                                </div>
                                            </div>
                                            <div className="space-y-2">
                                                <div className="flex items-center space-x-2">
                                                    <Checkbox id="min_tenure" checked={leaveTypeForm.min_tenure_months > 0} onCheckedChange={(c) => setLeaveTypeForm({ ...leaveTypeForm, min_tenure_months: c ? 12 : 0 })} />
                                                    <Label htmlFor="min_tenure">Requires minimum tenure</Label>
                                                </div>
                                                {leaveTypeForm.min_tenure_months > 0 && (
                                                    <div className="flex items-center gap-2 pl-6">
                                                        <Input type="number" min={1} className="w-24" value={leaveTypeForm.min_tenure_months} onChange={e => setLeaveTypeForm({ ...leaveTypeForm, min_tenure_months: parseInt(e.target.value) || 1 })} />
                                                        <span className="text-sm text-muted-foreground">months of service required</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <DialogFooter>
                                            <Button type="button" variant="outline" onClick={() => setIsLeaveTypeOpen(false)}>Cancel</Button>
                                            <Button type="submit" disabled={createLoading}>{createLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{editingLeaveType ? 'Update' : 'Create'}</Button>
                                        </DialogFooter>
                                    </form>
                                </DialogContent>
                            </Dialog>
                        </div>
                        <Card>
                            <CardContent className="pt-4 pb-4 px-4">
                                <div className="flex items-center gap-4">
                                    <div className="flex-1">
                                        <Label className="text-sm font-medium">Leave Balance Refresh Cycle</Label>
                                        <p className="text-xs text-muted-foreground mt-0.5">When leave balances reset for all employees across all leave types</p>
                                    </div>
                                    <Select value={leaveRefreshCycle} onValueChange={setLeaveRefreshCycle}>
                                        <SelectTrigger className="w-60">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="calendar_year">Every Calendar Year (Jan 1)</SelectItem>
                                            <SelectItem value="financial_year">Every Financial Year (Apr 1)</SelectItem>
                                            <SelectItem value="none">No Automatic Refresh</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <Button size="sm" onClick={handleSaveLeaveSettings} disabled={savingLeaveSettings}>
                                        {savingLeaveSettings ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="pt-4 pb-4 px-4">
                                <div className="flex items-center gap-4">
                                    <div className="flex-1">
                                        <Label className="text-sm font-medium">Allowed Optional Holidays</Label>
                                        <p className="text-xs text-muted-foreground mt-0.5">Configure the maximum number of optional holidays an employee can avail in a year</p>
                                    </div>
                                    <Input
                                        type="number"
                                        min={0}
                                        className="w-60"
                                        value={allowedOptionalHolidays}
                                        onChange={(e) => setAllowedOptionalHolidays(e.target.value)}
                                        placeholder="e.g. 2"
                                    />
                                    <Button size="sm" onClick={handleSaveLeaveSettings} disabled={savingLeaveSettings}>
                                        {savingLeaveSettings ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="p-0">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Name</TableHead>
                                            <TableHead>Code</TableHead>
                                            <TableHead>Days</TableHead>
                                            <TableHead>Status</TableHead>
                                            <TableHead className="w-[100px]">Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {leaveTypes.length === 0 ? (
                                            <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No leave types yet</TableCell></TableRow>
                                        ) : leaveTypes.map(lt => (
                                            <TableRow key={lt.id} className={!lt.is_active ? 'opacity-50' : ''}>
                                                <TableCell className="font-medium">
                                                    {lt.name}
                                                    {!lt.is_paid && <span className="ml-2 text-[10px] bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded font-medium">Unpaid</span>}
                                                </TableCell>
                                                <TableCell>{lt.code}</TableCell>
                                                <TableCell>{lt.days_allowed}</TableCell>
                                                <TableCell>
                                                    <span className={`text-xs font-medium ${lt.is_active ? 'text-green-600' : 'text-slate-400'}`}>{lt.is_active ? 'Active' : 'Inactive'}</span>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-1">
                                                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => handleEditLeaveType(lt)}><Edit className="h-4 w-4" /></Button>
                                                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10" onClick={() => handleDeleteLeaveType(lt)}><Trash2 className="h-4 w-4" /></Button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    </div>
                )}

                {/* ── Asset Categories ── */}
                {activeSection === 'assetcategories' && (
                    <div className="space-y-4">
                        <div className="flex items-start justify-between pb-4 border-b">
                            <div>
                                <h2 className="text-lg font-semibold text-slate-900">Asset Categories</h2>
                                <p className="text-sm text-muted-foreground mt-0.5">Manage IT asset categories for your organization</p>
                            </div>
                            <Dialog open={isAssetCatOpen} onOpenChange={(open) => { setIsAssetCatOpen(open); if (!open) { setEditingAssetCat(null); setAssetCatForm({ name: '', code: '', description: '', icon: '' }); } }}>
                                <DialogTrigger asChild>
                                    <Button size="sm"><Plus className="mr-2 h-4 w-4" />Add Category</Button>
                                </DialogTrigger>
                                <DialogContent>
                                    <DialogHeader>
                                        <DialogTitle>{editingAssetCat ? 'Edit Asset Category' : 'Add Asset Category'}</DialogTitle>
                                        <DialogDescription>{editingAssetCat ? 'Update category details' : 'Create a new asset category'}</DialogDescription>
                                    </DialogHeader>
                                    <form onSubmit={handleCreateOrUpdateAssetCat}>
                                        <div className="grid gap-4 py-4">
                                            <div className="space-y-2">
                                                <Label>Category Name *</Label>
                                                <Input value={assetCatForm.name} onChange={e => setAssetCatForm({ ...assetCatForm, name: e.target.value })} required placeholder="e.g. Laptop, Monitor" />
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Code</Label>
                                                <Input value={assetCatForm.code} onChange={e => setAssetCatForm({ ...assetCatForm, code: e.target.value })} placeholder="e.g. LAP" />
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Description</Label>
                                                <Input value={assetCatForm.description} onChange={e => setAssetCatForm({ ...assetCatForm, description: e.target.value })} />
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Icon</Label>
                                                <Select value={assetCatForm.icon} onValueChange={v => setAssetCatForm({ ...assetCatForm, icon: v })}>
                                                    <SelectTrigger><SelectValue placeholder="Select Icon" /></SelectTrigger>
                                                    <SelectContent>
                                                        {ICON_OPTIONS.map(opt => (
                                                            <SelectItem key={opt.value} value={opt.value}>
                                                                <div className="flex items-center gap-2"><opt.icon className="h-4 w-4" />{opt.label}</div>
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        </div>
                                        <DialogFooter>
                                            <Button type="button" variant="outline" onClick={() => setIsAssetCatOpen(false)}>Cancel</Button>
                                            <Button type="submit" disabled={createLoading}>{createLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{editingAssetCat ? 'Update' : 'Create'}</Button>
                                        </DialogFooter>
                                    </form>
                                </DialogContent>
                            </Dialog>
                        </div>
                        <Card>
                            <CardContent className="p-0">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Icon</TableHead>
                                            <TableHead>Name</TableHead>
                                            <TableHead>Code</TableHead>
                                            <TableHead>Description</TableHead>
                                            <TableHead className="w-[80px]">Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {assetCategories.length === 0 ? (
                                            <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No asset categories yet</TableCell></TableRow>
                                        ) : assetCategories.map(cat => {
                                            const IconComp = ICON_OPTIONS.find(o => o.value === cat.icon)?.icon || Package;
                                            return (
                                                <TableRow key={cat.id}>
                                                    <TableCell><IconComp className="h-5 w-5 text-muted-foreground" /></TableCell>
                                                    <TableCell className="font-medium">{cat.name}</TableCell>
                                                    <TableCell>{cat.code || '—'}</TableCell>
                                                    <TableCell className="max-w-[200px] truncate text-sm text-muted-foreground">{cat.description || '—'}</TableCell>
                                                    <TableCell>
                                                        <div className="flex items-center gap-1">
                                                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => handleEditAssetCat(cat)}><Edit className="h-4 w-4 text-slate-500" /></Button>
                                                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-destructive" onClick={() => handleDeleteAssetCat(cat.id)}><Trash2 className="h-4 w-4" /></Button>
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    </div>
                )}

                {/* ── Locations ── */}
                {activeSection === 'locations' && (
                    <div className="space-y-4">
                        <div className="flex items-start justify-between pb-4 border-b">
                            <div>
                                <h2 className="text-lg font-semibold text-slate-900">Office Locations</h2>
                                <p className="text-sm text-muted-foreground mt-0.5">Manage branch locations for geo-fenced attendance</p>
                            </div>
                            <Dialog open={isLocOpen} onOpenChange={(open) => { setIsLocOpen(open); if (!open) { setEditingLoc(null); setMapsUrl(''); setLocForm({ name: '', description: '', latitude: '', longitude: '', radius: 100 }); } }}>
                                <DialogTrigger asChild>
                                    <Button size="sm"><Plus className="mr-2 h-4 w-4" />Add Location</Button>
                                </DialogTrigger>
                                <DialogContent className="max-w-md">
                                    <DialogHeader>
                                        <DialogTitle>{editingLoc ? 'Edit Location' : 'Add Location'}</DialogTitle>
                                        <DialogDescription>Define a location with coordinates and radius for geofencing.</DialogDescription>
                                    </DialogHeader>
                                    <form onSubmit={handleCreateOrUpdateLoc}>
                                        <div className="grid gap-4 py-4">
                                            <div className="space-y-2">
                                                <Label>Location Name *</Label>
                                                <Input value={locForm.name} onChange={e => setLocForm({ ...locForm, name: e.target.value })} required placeholder="e.g. Head Office, Bangalore" />
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Description</Label>
                                                <Input value={locForm.description} onChange={e => setLocForm({ ...locForm, description: e.target.value })} placeholder="Brief details about the location" />
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Google Maps Link</Label>
                                                <div className="flex gap-2">
                                                    <Input 
                                                        placeholder="Paste Google Maps URL here..." 
                                                        value={mapsUrl}
                                                        onChange={e => setMapsUrl(e.target.value)}
                                                    />
                                                    <Button 
                                                        type="button" 
                                                        variant="secondary" 
                                                        onClick={handleResolveCoords}
                                                        disabled={resolvingCoords || !mapsUrl}
                                                    >
                                                        {resolvingCoords ? <Loader2 className="h-4 w-4 animate-spin" /> : "Fetch"}
                                                    </Button>
                                                </div>
                                                <p className="text-[10px] text-muted-foreground italic">Pasting a link will automatically extract latitude and longitude.</p>
                                            </div>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div className="space-y-2">
                                                    <Label>Latitude *</Label>
                                                    <Input type="number" step="any" value={locForm.latitude} onChange={e => setLocForm({ ...locForm, latitude: e.target.value })} required placeholder="12.9716" />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label>Longitude *</Label>
                                                    <Input type="number" step="any" value={locForm.longitude} onChange={e => setLocForm({ ...locForm, longitude: e.target.value })} required placeholder="77.5946" />
                                                </div>
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Radius (meters) *</Label>
                                                <Input type="number" min={10} value={locForm.radius} onChange={e => setLocForm({ ...locForm, radius: e.target.value })} required placeholder="100" />
                                                <p className="text-[10px] text-muted-foreground italic">Employees must be within this distance to check-in/out.</p>
                                            </div>
                                        </div>
                                        <DialogFooter>
                                            <Button type="button" variant="outline" onClick={() => setIsLocOpen(false)}>Cancel</Button>
                                            <Button type="submit" disabled={createLoading}>{createLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{editingLoc ? 'Update' : 'Create'}</Button>
                                        </DialogFooter>
                                    </form>
                                </DialogContent>
                            </Dialog>
                        </div>
                        <Card>
                            <CardContent className="p-0">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Location</TableHead>
                                            <TableHead>Coordinates</TableHead>
                                            <TableHead>Radius</TableHead>
                                            <TableHead className="w-[80px]">Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {locations.length === 0 ? (
                                            <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">No locations defined yet</TableCell></TableRow>
                                        ) : locations.map(loc => (
                                            <TableRow key={loc.id}>
                                                <TableCell>
                                                    <div className="flex flex-col">
                                                        <span className="font-medium flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-primary" />{loc.name}</span>
                                                        <span className="text-xs text-muted-foreground truncate max-w-[200px]">{loc.description || 'No description'}</span>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="text-sm font-mono text-slate-500">
                                                    {loc.latitude.toFixed(6)}, {loc.longitude.toFixed(6)}
                                                </TableCell>
                                                <TableCell className="text-sm">{loc.radius}m</TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-1">
                                                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => handleEditLoc(loc)}><Edit className="h-4 w-4 text-slate-500" /></Button>
                                                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-destructive" onClick={() => handleDeleteLoc(loc.id)}><Trash2 className="h-4 w-4" /></Button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    </div>
                )}

                {/* ── Attendance Settings ── */}
                {activeSection === 'attendance' && (
                    <div className="space-y-4">
                        <div className="flex items-start justify-between pb-4 border-b mb-6">
                            <div>
                                <h2 className="text-lg font-semibold text-slate-900">Attendance Settings</h2>
                                <p className="text-sm text-muted-foreground mt-0.5">Configure shifts, grace periods and regularization rules</p>
                            </div>
                            <Button onClick={handleSaveAttendanceConfig} disabled={savingAttendance} size="sm">
                                {savingAttendance ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                                Save Changes
                            </Button>
                        </div>
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-base">Shift & Grace Period</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Default Shift Start</Label>
                                        <Input 
                                            type="time" 
                                            value={attendanceConfig?.shift_start || '09:00'} 
                                            onChange={(e) => setAttendanceConfig({ ...attendanceConfig, shift_start: e.target.value })} 
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Default Shift End</Label>
                                        <Input 
                                            type="time" 
                                            value={attendanceConfig?.shift_end || '18:00'} 
                                            onChange={(e) => setAttendanceConfig({ ...attendanceConfig, shift_end: e.target.value })} 
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label>Grace Period (Minutes)</Label>
                                    <Input 
                                        type="number" 
                                        value={attendanceConfig?.grace_period_mins || 15} 
                                        onChange={(e) => setAttendanceConfig({ ...attendanceConfig, grace_period_mins: parseInt(e.target.value) || 0 })} 
                                    />
                                    <p className="text-xs text-muted-foreground">Employees punching in after (Shift Start + Grace Period) will be marked as LATE.</p>
                                </div>
                                <div className="space-y-3 pt-2">
                                    <Label>Working Days</Label>
                                    <div className="flex flex-wrap gap-4">
                                        {[
                                            { label: 'Mon', value: 1 },
                                            { label: 'Tue', value: 2 },
                                            { label: 'Wed', value: 3 },
                                            { label: 'Thu', value: 4 },
                                            { label: 'Fri', value: 5 },
                                            { label: 'Sat', value: 6 },
                                            { label: 'Sun', value: 0 },
                                        ].map((day) => (
                                            <div key={day.value} className="flex items-center space-x-2">
                                                <Checkbox 
                                                    id={`day-${day.value}`}
                                                    checked={(attendanceConfig?.working_days || [1,2,3,4,5]).includes(day.value)}
                                                    onCheckedChange={(checked) => {
                                                        const current = attendanceConfig?.working_days || [1,2,3,4,5];
                                                        const next = checked 
                                                            ? [...current, day.value]
                                                            : current.filter(d => d !== day.value);
                                                        setAttendanceConfig({ ...attendanceConfig, working_days: next });
                                                    }}
                                                />
                                                <Label htmlFor={`day-${day.value}`} className="font-normal">{day.label}</Label>
                                            </div>
                                        ))}
                                    </div>
                                    <p className="text-xs text-muted-foreground">Only selected days will appear in the employee attendance history table.</p>
                                </div>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-base">Regularization Rules</CardTitle>
                                <CardDescription>Control how employees can request corrections to their attendance records</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="flex items-center space-x-2">
                                    <Checkbox
                                        id="allow_regularization"
                                        checked={attendanceConfig?.allow_regularization ?? true}
                                        onCheckedChange={(c) => setAttendanceConfig({ ...attendanceConfig, allow_regularization: c })}
                                    />
                                    <Label htmlFor="allow_regularization">Allow Employees to request Regularization</Label>
                                </div>
                                {attendanceConfig?.allow_regularization !== false && (
                                    <div className="space-y-2 pt-2">
                                        <Label>Max Regularization Days per Month</Label>
                                        <Input 
                                            type="number" 
                                            className="max-w-xs"
                                            value={attendanceConfig?.max_regularization_days || 3} 
                                            onChange={(e) => setAttendanceConfig({ ...attendanceConfig, max_regularization_days: parseInt(e.target.value) || 0 })} 
                                        />
                                        <p className="text-xs text-muted-foreground">This is the default limit for all employees. You can override this for specific employees in their profiles.</p>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                )}

                {activeSection === 'kiosk' && (
                    <div className="space-y-6">
                        <div className="flex items-start justify-between pb-4 border-b mb-6">
                            <div>
                                <h2 className="text-lg font-semibold text-slate-900">Kiosk / Face Recognition Attendance</h2>
                                <p className="text-sm text-muted-foreground mt-0.5">Manage face recognition enrollment for the Teamly FRA kiosk device</p>
                            </div>
                        </div>

                        <Card>
                            <CardHeader>
                                <CardTitle>Bulk Face Enrollment</CardTitle>
                                <CardDescription>
                                    Enroll all active employees using their existing profile pictures. Run this once after setup, and again whenever new employees are added. Employees without a profile picture will be skipped.
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <Button
                                    onClick={async () => {
                                        setEnrolling(true);
                                        setEnrollResult(null);
                                        try {
                                            const res = await kioskAPI.enrollAll();
                                            setEnrollResult({ success: true, ...res.data });
                                            toast.success(`Face enrollment complete — ${res.data.enrolled} enrolled, ${res.data.skipped} skipped`);
                                        } catch (err) {
                                            const msg = err.response?.data?.detail || 'Enrollment failed';
                                            setEnrollResult({ success: false, message: msg });
                                            toast.error(msg);
                                        } finally {
                                            setEnrolling(false);
                                        }
                                    }}
                                    disabled={enrolling}
                                >
                                    {enrolling && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                    {enrolling ? 'Enrolling faces…' : 'Enroll All Faces'}
                                </Button>

                                {enrollResult && (
                                    <div className={`rounded-md p-4 text-sm ${enrollResult.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                                        {enrollResult.success ? (
                                            <ul className="space-y-1">
                                                <li><strong>Enrolled:</strong> {enrollResult.enrolled}</li>
                                                <li><strong>Skipped</strong> (no photo / already enrolled): {enrollResult.skipped}</li>
                                                <li><strong>Failed:</strong> {enrollResult.failed}</li>
                                            </ul>
                                        ) : (
                                            <p>{enrollResult.message}</p>
                                        )}
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle>Auto-Enrollment</CardTitle>
                                <CardDescription>
                                    When an employee's profile picture is updated, their face embedding is automatically re-enrolled. No manual action needed.
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <p className="text-sm text-slate-600 flex items-center gap-2">
                                    <span className="inline-block h-2 w-2 rounded-full bg-green-500" />
                                    Auto-enrollment on profile picture update is active
                                </p>
                            </CardContent>
                        </Card>

                        {process.env.NODE_ENV === 'development' && (
                            <Card className="border-red-200 bg-red-50">
                                <CardHeader>
                                    <CardTitle className="text-red-700">⚠ Dev Only — Reset Today's Attendance</CardTitle>
                                    <CardDescription className="text-red-600">
                                        Deletes all attendance records for today. Only visible in local development. Not available in production.
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <Button
                                        variant="destructive"
                                        onClick={async () => {
                                            if (!window.confirm('Delete ALL attendance records for today? This cannot be undone.')) return;
                                            setResetting(true);
                                            try {
                                                const res = await kioskAPI.resetAttendance();
                                                toast.success(`Deleted ${res.data.deleted} attendance record(s) for ${res.data.date}`);
                                            } catch (err) {
                                                toast.error(err.response?.data?.detail || 'Reset failed');
                                            } finally {
                                                setResetting(false);
                                            }
                                        }}
                                        disabled={resetting}
                                    >
                                        {resetting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                        {resetting ? 'Resetting…' : 'Reset Today\'s Attendance'}
                                    </Button>
                                </CardContent>
                            </Card>
                        )}
                    </div>
                )}

                </main>
            </div>

            <ConfirmationDialog {...dialogProps} />
        </div>
    );


};

export default Settings;
